/**
 * The bar's ⋯ menu, wired: which acts it may offer, the confirm each one raises, and the two
 * requests behind them.
 *
 * `model/planLifecycleMenu` is the whole of the decision. This owns the read, the mutations, the
 * open dialog and where the trainer lands afterwards, and decides nothing.
 *
 * ── The acts are irreversible, so every one of them goes through the confirm ─────────────────
 * There is no quiet path here: an entry sets `open`, the dialog states what the act does and cannot
 * undo, and only its confirm sends anything.
 *
 * ── After a deletion the plan is GONE, so the screen may not stay on it ──────────────────────
 * The route would immediately refetch a template the server no longer has and land on the builder's
 * not-found state, which reads as a failure rather than as the thing the trainer just asked for. So
 * the navigation to `/plans` happens on success, and the library's cache entry is dropped on the
 * way — a list still carrying the row would draw it until its own window expired.
 *
 * ── An ending does NOT navigate, and it invalidates FOUR reads ───────────────────────────────
 * The plan still exists and the trainer is still looking at it; what changes is that it is over. So
 * everything that describes it has to be told, and three of the four keys share no prefix with each
 * other:
 *
 * · the assignment LIST, so this menu re-reads its own facts and stops offering the act it took;
 * · the assignment's EXECUTION SUMMARY, which is what the client's plan calendar draws its header
 *   from — invalidating only the list left that header reading `Aktywny` over a masked future,
 *   which is the disagreement `usePlanActs`'s own header says it was written to prevent;
 * · the CLIENT's assignment list, which the calendar's plan picker draws;
 * · this plan's own DOCUMENT, because the bar's chips are drawn from it and an ended plan is a
 *   different plan as far as they are concerned.
 *
 * ── A refusal is SHOWN, never swallowed ─────────────────────────────────────────────────────
 * The dialog stays open and carries the sentence. Closing it on an error would leave the trainer
 * looking at an unchanged screen with no account of why.
 */

import { useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { invalidateAssignmentExecution } from '~/entities/plan-assignment';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  planAssignmentListQueryKey,
  planTemplateAssignmentsQueryOptions,
  planTemplateFullRoot,
} from '~/entities/plan-draft';
import { useDeletePlanTemplateId } from '~/shared/api/generated/endpoints/plan-template/plan-template';
import {
  getGetPlanAssignmentCustomerCustomerIdQueryKey,
  usePostPlanAssignmentIdComplete,
  usePostPlanAssignmentIdEndEarly,
} from '~/shared/api/generated/endpoints/plan-assignment/plan-assignment';
import {
  planLifecycleMenu,
  planLifecyclePrompt,
  type PlanLifecycleAct,
  type PlanLifecycleEntry,
  type PlanLifecyclePrompt,
} from '../model/planLifecycleMenu';
import { useContentLanguage } from '~/shared/i18n/contentLanguage';

/**
 * Everything the app caches off `plan-template`. `planTemplateFullRoot` and the plans list's own key
 * both start here, which is what makes one invalidation enough after a deletion.
 */
const PLAN_TEMPLATE_ROOT = ['plan-template'] as const;

export interface PlanLifecycleState {
  /** The acts the ⋯ menu draws below its two ordinary entries. Empty is normal. */
  readonly entries: readonly PlanLifecycleEntry[];
  /**
   * The open confirm, or `null` — already carrying the refusal and the in-flight flag, so the
   * surface renders it and nothing else.
   */
  readonly prompt: (PlanLifecyclePrompt & { readonly busy: boolean }) | null;
  readonly busy: boolean;
  readonly start: (act: PlanLifecycleAct) => void;
  readonly confirm: () => void;
  readonly dismiss: () => void;
}

export interface PlanLifecycleInputs {
  /** The template on screen, or `null` on a plan that has never been saved. */
  readonly planId: number | null;
  /** Whether this template is a library entry rather than a client's fork. */
  readonly isLibraryEntry: boolean;
  /** Whether the plan books no dates — picks `endEarly`'s sentence. R8. */
  readonly isSelfPaced: boolean;
  /** The bar's live region, so an act's outcome is announced rather than only drawn. */
  readonly announce: (message: string) => void;
}

export function usePlanLifecycle(inputs: PlanLifecycleInputs): PlanLifecycleState {
  const { planId, isLibraryEntry, isSelfPaced, announce } = inputs;
  const { t } = useTranslation();
  const contentLang = useContentLanguage();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [open, setOpen] = useState<PlanLifecycleAct | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);

  const assignments = useQuery({
    ...planTemplateAssignmentsQueryOptions(planId ?? 0),
    enabled: planId !== null,
  });

  const remove = useDeletePlanTemplateId();
  const endEarly = usePostPlanAssignmentIdEndEarly();
  const complete = usePostPlanAssignmentIdComplete();
  const busy = remove.isPending || endEarly.isPending || complete.isPending;

  const facts = assignments.data ?? { fork: null, followerCount: 0 };
  const entries = planLifecycleMenu(
    {
      assignments: facts,
      isLibraryEntry,
      // A plan with no id has nothing on the server, and a read that has not answered is not an
      // answer. Both are the same "not yet" as far as the menu is concerned.
      pending: planId === null || assignments.isPending,
    },
    t,
  );

  /**
   * @param failure the sentence this act's refusal is worded with. A deletion and a change to a
   *   running assignment are not the same failure and do not read the same.
   * @param done what to announce on success, or `null` for the deletion — see below.
   */
  const settled = (failure: string, done: string | null, after: () => void) => ({
    onSuccess: () => {
      setOpen(null);
      setRefusal(null);
      /* The deletion announces NOTHING here, and that is deliberate rather than an omission. Its
       * `onSuccess` navigates in the same handler: the live region it would write into belongs to
       * the builder, which is unmounted before the region ever renders non-empty — measured at
       * /plans within 77ms and focus on its heading at 122ms. The destination is the announcement,
       * and `AppShell` speaks it by moving focus to that screen's own `h1`. */
      if (done !== null) announce(done);
      after();
    },
    onError: () => {
      /* SHOWN and SPOKEN. The refusal is concatenated onto the dialog's description, which is not a
       * live region — a screen-reader user would get a dialog that stays open, buttons that come
       * back, and no account of why. `usePlanActs` does both for the same reason. */
      setRefusal(failure);
      announce(failure);
    },
  });

  const confirm = () => {
    if (open === null || planId === null) return;
    setRefusal(null);

    if (open === 'delete') {
      remove.mutate(
        { id: planId },
        settled(t('web:planBuilder.planMenu.deleteFailed'), null, () => {
          /* The deleted plan's own entry is REMOVED, and then deliberately held out of the
           * invalidation that follows. Both halves are needed: the builder is still mounted at this
           * moment, so it re-subscribes the instant the entry is dropped, and a prefix invalidation
           * that reached the same key would answer that subscription with a read for a row the
           * server has just removed — measured, as a `GET /plan-template/41/full` chasing its own
           * `DELETE`. Everything ELSE cut from that endpoint is stale and is told so, the plans
           * list included, whose own key lives in a page this one may not import. */
          const dead = planTemplateFullRoot(planId);
          queryClient.removeQueries({ queryKey: dead });
          void queryClient.invalidateQueries({
            predicate: (query) =>
              query.queryKey[0] === PLAN_TEMPLATE_ROOT[0] &&
              !(query.queryKey[1] === dead[1] && query.queryKey[2] === dead[2]),
          });
          void navigate({ to: '/plans' });
        }),
      );
      return;
    }

    const fork = facts.fork;
    if (fork === null) return;
    const request = open === 'endEarly' ? endEarly : complete;
    const done =
      open === 'endEarly'
        ? t('web:planCalendar.planAct.done.endedEarly')
        : t('web:planCalendar.planAct.done.completed');
    request.mutate(
      // `lang` for the reason `usePlanActs` passes it: so the server words its own refusals in the
      // trainer's language, whether or not this screen chooses to print them.
      { id: fork.assignmentId, params: { lang: contentLang } },
      settled(t('web:planCalendar.planAct.failed'), done, () => {
        void queryClient.invalidateQueries({ queryKey: planAssignmentListQueryKey });
        invalidateAssignmentExecution(queryClient, fork.assignmentId);
        if (fork.customerId !== null) {
          void queryClient.invalidateQueries({
            queryKey: getGetPlanAssignmentCustomerCustomerIdQueryKey(fork.customerId),
          });
        }
        if (planId !== null) {
          void queryClient.invalidateQueries({ queryKey: planTemplateFullRoot(planId) });
        }
      }),
    );
  };

  /* The refusal joins the question rather than replacing it: a dialog that dropped its own
   * description on an error would leave the trainer confirming an act nothing on screen still
   * describes. `ConfirmDialog` takes one paragraph, so the two sentences share it. */
  const base =
    open === null
      ? null
      : planLifecyclePrompt(open, { followerCount: facts.followerCount, isSelfPaced }, t);

  return {
    entries,
    prompt:
      base === null
        ? null
        : {
            ...base,
            description:
              refusal === null ? base.description : `${base.description} ${refusal}`,
            busy,
          },
    busy,
    start: (act) => {
      setRefusal(null);
      setOpen(act);
    },
    confirm,
    dismiss: () => {
      setOpen(null);
      setRefusal(null);
    },
  };
}
